import axiosInstance from "../axios/axiosInstance";

interface BodyProps {
  generator: string;
  tagGroups: any[];
  customPrompt: string;
  queueType: number;
  customNegative?: string | null;
  cfgScale?: number | null;
  denoisingStrength?: number | null;
  seed?: number | null;
  batch: number;
}

interface PollBodyProps {
  jobId: string;
}

interface UpscaleBodyProps {
  upscale: boolean;
  upscale_amount: number;
  generation: string;
  queueType: number;
}

export interface FreeQueuePositionPollBodyProps {
  jobId: string;
}

interface StartGenerateImageResponse {
  response: {
    jobId?: string;
    runpodJobId?: string;
    [key: string]: any; // Assuming image can have any other properties
  } | null;
  error: any;
}

export interface MediaResult {
  id: string;
  type: "image" | "gif"; // Ensure type is either "image" or "gif"
  imageUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
}

export interface GenerateMediaSuccessResponse {
  result?: MediaResult[];
  jobId: string;
  position?: number;
  status: string;
  creditChange?: any;
}

export interface PollGenerateMediaResponse {
  response: GenerateMediaSuccessResponse | null;
  error: any;
}

export interface QueuePositionResponse {
  queue: string;
  position: number;
}

interface UpscaleBodyProps {
  upscale: boolean;
  upscale_amount: number;
  generation: string;
}

interface UpscaleResponse {
  response: {
    jobId?: string;
    [key: string]: any; // Assuming response can have other properties
  } | null;
  error: any;
}

export const getModel = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/generate/model/${id}`);
    return { response, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error };
  }
};

export const getGifModel = async () => {
  try {
    const response = await axiosInstance.get(`/generate/gif-model`);
    return { response, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error };
  }
};

export const startGenerate = async (body: BodyProps): Promise<StartGenerateImageResponse> => {
  try {
    const response = await axiosInstance.post(`/generate/start/`, body);
    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};

export const startGenerateGif = async (body: any): Promise<any> => {
  try {
    const response = await axiosInstance.post(`/generate/video`, body);
    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};

export const pollGenerationJob = async (
  body: PollBodyProps,
): Promise<PollGenerateMediaResponse> => {
  try {
    const response = await axiosInstance.post(`/generate/poll/`, body);
    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};

export const pollQueuePositionFree = async (
  jobId: string,
): Promise<{ response: QueuePositionResponse | null; error: any | null }> => {
  try {
    const response = await axiosInstance.get(`/queue/position/free/${jobId}`);
    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};

export const pollQueuePositionPremium = async (
  jobId: string,
): Promise<{ response: QueuePositionResponse | null; error: any | null }> => {
  try {
    const response = await axiosInstance.get(`/queue/position/premium/${jobId}`);
    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};

export const startUpscaleImage = async (body: UpscaleBodyProps): Promise<UpscaleResponse> => {
  try {
    // Enforce upscale_amount to be one of 1, 2, or 3
    if (![1, 2, 3].includes(body.upscale_amount)) {
      body.upscale_amount = 2; // Default to 2x if invalid value is provided
    }

    const urlParts = body.generation.split("/");
    let generationId = urlParts[urlParts.length - 1].replace(".png", "");
    // TODO: batch upscale
    const response = await axiosInstance.post(`/generate/upscale`, {
      upscale: true, // Assuming upscale is always true when this function is called
      upscale_amount: body.upscale_amount,
      generation: generationId.replace(".jpg", ""),
      queueType: body.queueType,
      batch: 1,
    });

    return { response: response.data, error: null };
  } catch (error) {
    console.log(error);
    return { response: null, error: error };
  }
};
